import React, { useEffect, useMemo, useState } from "react";
import { FaRegUserCircle } from "react-icons/fa";
import { NavLink } from "react-router-dom";
import axios from "axios";
import apiKey from "../../../config/apiKey";
import Pusher from "pusher-js";
import Echo from "laravel-echo";
import { useTranslation } from "react-i18next";

// inline skeleton + helpers (no new files)
const Skeleton = ({ className = "" }) => (
  <div className={`animate-pulse bg-gray-100 rounded ${className}`} />
);

const RelativeTime = ({ date }) => {
  const { t } = useTranslation();
  const d = new Date(date);
  const diff = (Date.now() - d.getTime()) / 1000;
  const mins = Math.floor(diff / 60);
  const hours = Math.floor(mins / 60);
  const days = Math.floor(hours / 24);
  let label = d.toLocaleString();
  if (mins < 1) label = t("adminDash.notification.justNow");
  else if (mins < 60) label = t("adminDash.notification.minsAgo", { count: mins });
  else if (hours < 24) label = t("adminDash.notification.hoursAgo", { count: hours });
  else if (days < 7) label = t("adminDash.notification.daysAgo", { count: days });
  return <span title={d.toLocaleString()} className="text-[12px] text-gray-500">{label}</span>;
};

const groupByDay = (items) => {
  const fmt = (x) =>
    new Date(x).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
  return items.reduce((acc, n) => {
    const key = fmt(n.created_at);
    (acc[key] ||= []).push(n);
    return acc;
  }, {});
};

export default function AllNotification() {
  const { t } = useTranslation();
  const admin = useMemo(() => JSON.parse(localStorage.getItem("edo-admin")), []);
  const [loading, setLoading] = useState(true);
  const [notifications, setNotifications] = useState([]);
  const [error, setError] = useState("");

  const fetchNotifications = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${apiKey}/api/edolutions/admin/notifications`, {
        headers: { Authorization: `Bearer ${admin?.token}` },
      });
      setNotifications(Array.isArray(res.data) ? res.data : []);
    } catch (e) {
      console.error("Error fetching notifications:", e);
      setError(t("adminDash.notification.loadFailed"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [admin?.token]);

  // Realtime listener with cleanup
  useEffect(() => {
    if (!admin?.token || !admin?.admin?.id) return;
    window.Pusher = Pusher;

    const echo = new Echo({
      broadcaster: "pusher",
      key: "301d873c1e8e09e9534c",
      cluster: "ap2",
      forceTLS: true,
      encrypted: true,
      authEndpoint: `${apiKey}/broadcasting/auth`,
      auth: { headers: { Authorization: `Bearer ${admin.token}` } },
    });

    const channel = `notification-channel.admin.${admin.admin.id}`;
    echo.private(channel).listen("NotificationAdminEvent", (e) => {
      if (e?.data) setNotifications((prev) => [e.data, ...prev]);
    });

    return () => {
      try {
        echo.leave(channel);
        echo.disconnect();
      } catch {}
    };
  }, [admin?.token, admin?.admin?.id]);

  const grouped = groupByDay(notifications);

  return (
    <div className="flex items-center xl:p-5 sm:p-0 justify-center">
      <div className="w-full border rounded-3xl shadow-sm bg-[#F9FAFB]">
        {/* Tabs header (pill style) */}
        <div className="px-6 pt-6 pb-2 bg-gradient-to-r from-[#F8FFFE] to-[#F5FAFF] rounded-t-3xl">
          <h1 className="text-2xl font-bold text-gray-800">{t("adminDash.notifications")}</h1>
          <p className="text-xs text-gray-500 mt-1">{t("adminDash.notification.adminFeed")}</p>
          <div className="mt-5 flex flex-wrap items-center gap-2">
            <NavLink to=".././all-notification" className={({ isActive }) =>
              `inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                isActive ? "bg-[#EE5F2B] text-white" : "bg-white text-gray-700 border border-gray-200 hover:bg-orange-400/10 hover:text-orange-400"
              }`
            }>
              {t("adminDash.studentNoti.all")}
            </NavLink>
            {/* <NavLink to=".././newNotification" className={({ isActive }) =>
              `inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                isActive ? "bg-[#EE5F2B] text-white" : "bg-white text-gray-700 border border-gray-200 hover:bg-orange-400/10 hover:text-orange-400"
              }`
            }>
              New
            </NavLink> */}
            <NavLink to=".././unread-notification" className={({ isActive }) =>
              `inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium transition-colors ${
                isActive ? "bg-[#EE5F2B] text-white" : "bg-white text-gray-700 border border-gray-200 hover:bg-orange-400/10 hover:text-orange-400"
              }`
            }>
              {t("adminDash.studentNoti.unread")}
            </NavLink>
          </div>
        </div>

        {/* Body */}
        <div className="px-6 pb-10">
          {loading && (
            <div className="space-y-3">
              <Skeleton className="h-5 w-28" />
              {[...Array(4)].map((_, i) => (
                <div className="pl-9" key={i}>
                  <Skeleton className="h-20 w-full" />
                </div>
              ))}
            </div>
          )}

          {!loading && error && (
            <div className="py-8 text-center text-gray-600">{error}</div>
          )}

          {!loading && !error && notifications.length === 0 && (
            <div className="py-8 text-center text-gray-500">{t("adminDash.notification.noNotificationsYet")}</div>
          )}

          {!loading && !error && notifications.length > 0 && (
            <div className="relative">
              {/* vertical line */}
              <div className="absolute left-3 top-0 bottom-0 w-px bg-gray-200" />
              {Object.entries(grouped).map(([day, items]) => (
                <div key={day} className="mb-6">
                  {/* day divider */}
                  <div className="flex items-center gap-3 my-4">
                    <div className="h-px bg-gray-200 flex-1" />
                    <span className="text-[12px] text-gray-500 px-2">{day}</span>
                    <div className="h-px bg-gray-200 flex-1" />
                  </div>

                  <div className="space-y-3">
                    {items.map((n) => (
                      <div className="relative pl-9" key={n.id}>
                        <div className="absolute left-2 top-2 w-2.5 h-2.5 rounded-full bg-[#EE5F2B] ring-2 ring-white shadow" />
                        <div className="rounded-xl border bg-white shadow-sm p-4">
                          <div className="flex gap-3">
                            <FaRegUserCircle className="h-8 w-8 text-gray-500 mt-0.5" />
                            <div className="flex-1">
                              <p className="text-[14px] font-semibold text-gray-800">{n.message}</p>
                              <RelativeTime date={n.created_at} />
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
